// ─────────────────────────────────────────────────────────────────────────────
// Single source of truth for all loan stage definitions in the frontend.
// Adding a new stage anywhere in the app should require editing only this
// file. See STAGE_ORDER, *_STAGES groupings, and STAGE_LABELS at the bottom.
// ─────────────────────────────────────────────────────────────────────────────

// Canonical status → display config. bg = background, text = text color.
export const STATUS_COLORS = {
  // Secondary-palette scheme: SOLID pills — bg = the main color, text =
  // black or white chosen per color for contrast. (Lighter mains — Orange,
  // Coral, Cyan, Teal, Tan, Green — take #1a1a1a; deeper mains — Slate,
  // Indigo, Violet, Magenta, Dark, Red — take #ffffff.) The bg color also
  // doubles as the stage ACCENT for dots / bars / borders.
  // Canonical values
  new:                  { bg: '#64748B', text: '#ffffff', label: 'Pre-Qualified' },     // Slate
  submitted:            { bg: '#0EA5E9', text: '#1a1a1a', label: 'Verification' },       // Cyan
  in_review:            { bg: '#4F46E5', text: '#ffffff', label: 'In Review' },          // Indigo
  approved:             { bg: '#acf44e', text: '#1a1a1a', label: 'Approved' },           // Doorly green (semantic)
  home_shopping:        { bg: '#7C58F5', text: '#ffffff', label: 'Shopping' },           // Violet
  offer_requested:      { bg: '#FF7A1A', text: '#1a1a1a', label: 'Offer Requested' },    // Orange
  offer_submitted:      { bg: '#FF6B9D', text: '#1a1a1a', label: 'Offer Submitted' },    // Coral
  offer_accepted:       { bg: '#D946C7', text: '#ffffff', label: 'Offer Accepted' },     // Magenta
  option_period:        { bg: '#FFDD86', text: '#1a1a1a', label: 'Option Period' },      // Tan
  under_contract:       { bg: '#F0FDFA', text: '#0F766E', label: 'Under Contract', shortLabel: 'UC' }, // Teal-tint (NC task window)
  closing:              { bg: '#14B8A6', text: '#1a1a1a', label: 'Closing' },            // Teal
  closed:               { bg: '#111827', text: '#ffffff', label: 'Closed' },             // Dark (semantic)
  // Aliases
  not_started:          { bg: '#64748B', text: '#ffffff', label: 'Pre-Qualified' },
  pre_qualified:        { bg: '#64748B', text: '#ffffff', label: 'Pre-Qualified' },
  started:              { bg: '#64748B', text: '#ffffff', label: 'Pre-Qualified' },
  registered:           { bg: '#64748B', text: '#ffffff', label: 'Pre-Qualified' },
  applied:              { bg: '#0EA5E9', text: '#1a1a1a', label: 'Verification' },
  conditional_approval: { bg: '#acf44e', text: '#1a1a1a', label: 'Cond. Approved' },     // Doorly green (semantic)
  clear_to_close:       { bg: '#4F46E5', text: '#ffffff', label: 'Clear to Close' },     // Indigo (reuse)
  funded:               { bg: '#111827', text: '#ffffff', label: 'Closed' },             // Dark (semantic)
  on_hold:              { bg: '#ff082f', text: '#ffffff', label: 'On Hold' },            // red (semantic)
  not_moving_forward:   { bg: '#ff082f', text: '#ffffff', label: 'Not Moving Forward' }, // red (semantic)
  archived:             { bg: '#f3f4f6', text: '#6b7280', label: 'Archived' },
  adverse:              { bg: '#fff0f3', text: '#ff082f', label: 'Adverse' },            // red (semantic)
  // adverse_pending — Atlas returned ineligible / sub-$150k at ReadyCheck
  // submit. NOT a decline; the file requires human review before any
  // adverse-action communication. Amber palette so it reads as "needs
  // attention" rather than red-flag terminal. System-set only — NOT
  // surfaced in MOVE_STAGE_OPTIONS so admins can't move borrowers TO it
  // via the dropdown.
  adverse_pending:      { bg: '#fbbf24', text: '#92400e', label: 'Adverse Pending' },
};

export const PIPELINE_STAGE_ORDER = [
  // ⚠️ in_progress ADDED — it was missing HERE and in the backend's STAGE_ORDER,
  // while live code writes it (utils/reopenFileCycle sets it when a file
  // restarts for a new cycle) and this very file's own status map renders it.
  // The backend list is the source of truth and says to keep this in lockstep;
  // backend/test/stageDeclaration.test.js now checks that both agree.
  'not_started', 'in_progress', 'pre_qualified', 'started', 'registered', 'new',
  'submitted', 'applied',
  'in_review',
  // adverse_pending sits between submitted and approved in the pipeline —
  // ReadyCheck couldn't auto-approve (ineligible or sub-$150k) and the
  // file is parked here pending human review.
  'adverse_pending',
  'approved', 'conditional_approval',
  'home_shopping',
  'offer_requested',
  'offer_submitted',
  'offer_accepted',
  'option_period',
  'closing', 'clear_to_close',
  'closed', 'funded',
  // ⚠️ adverse AND not_moving_forward ADDED. adverse_pending was already placed
  // mid-pipeline above, deliberately — a parked file belongs where it was
  // parked, and this list drives DISPLAY order. Its two terminal siblings had
  // no entry at all, so getStageIndex returned 999 for them and they sorted
  // after everything including on_hold. not_moving_forward is the most common
  // status in production.
  //
  // 🔑 POSITION NEED NOT MATCH THE BACKEND, membership must. The backend's
  // STAGE_ORDER declares the vocabulary and appends terminals; this one orders a
  // pipeline. backend/test/stageDeclaration.test.js checks membership only.
  'adverse', 'not_moving_forward',
  'on_hold',
];

export const getStageIndex = (status) => {
  const idx = PIPELINE_STAGE_ORDER.indexOf(status);
  return idx === -1 ? 999 : idx;
};

// shortLabel is the compact form used on dense filter rows (Borrower
// Pipeline page status chips) so all 11 stages fit on one line at typical
// desktop widths. Consumers that have room (table StatusBadge, status
// dropdowns, breadcrumbs) keep using the full label.
export const MOVE_STAGE_OPTIONS = [
  // color/textColor here are the SOLID treatment used when a chip is the
  // selected stage (main color + black/white per contrast, matching
  // STATUS_COLORS). Unselected chips render white/gray in the consumers.
  { value: 'new',             label: 'Pre-Qualified',   shortLabel: 'Pre-Qual',  color: '#64748B', textColor: '#ffffff' },
  { value: 'submitted',       label: 'Verification',    shortLabel: 'Verify',    color: '#0EA5E9', textColor: '#1a1a1a' },
  // in_review intentionally NOT listed — ReadyCheck owns that transition now,
  // so admins shouldn't be able to manually move borrowers TO it. The
  // STATUS_COLORS entry + PIPELINE_STAGE_ORDER position are kept so any
  // historical in_review rows still render and sort correctly.
  { value: 'approved',        label: 'Approved',        shortLabel: 'Approved',  color: '#acf44e', textColor: '#1a1a1a' },
  { value: 'home_shopping',   label: 'Shopping',        shortLabel: 'Shopping',  color: '#7C58F5', textColor: '#ffffff' },
  { value: 'offer_requested', label: 'Offer Requested', shortLabel: 'Offer Req', color: '#FF7A1A', textColor: '#1a1a1a' },
  { value: 'offer_submitted', label: 'Offer Submitted', shortLabel: 'Offer Sub', color: '#FF6B9D', textColor: '#1a1a1a' },
  { value: 'offer_accepted',  label: 'Offer Accepted',  shortLabel: 'Offer Acc', color: '#D946C7', textColor: '#ffffff' },
  { value: 'option_period',   label: 'Option Period',   shortLabel: 'Opt. Per.', color: '#FFDD86', textColor: '#1a1a1a' },
  { value: 'closing',         label: 'Closing',         shortLabel: 'Closing',   color: '#14B8A6', textColor: '#1a1a1a' },
  { value: 'clear_to_close',  label: 'Clear to Close',  shortLabel: 'CTC',       color: '#4F46E5', textColor: '#ffffff' },
  { value: 'closed',          label: 'Closed',          shortLabel: 'Closed',    color: '#111827', textColor: '#ffffff' },
];

// Alias used by pipeline/borrowers pages
export const PIPELINE_STAGE_OPTIONS = MOVE_STAGE_OPTIONS;

// ─────────────────────────────────────────────────────────────────────────────
// Stage groupings — the canonical sets used across multiple pages.
// Add a new stage to STAGE_ORDER + STATUS_COLORS + MOVE_STAGE_OPTIONS, then
// update the relevant grouping(s) below; every consumer page picks it up.
// ─────────────────────────────────────────────────────────────────────────────

// Full ordered list of every stage from start to terminal. Alias of
// PIPELINE_STAGE_ORDER above; the new name is preferred going forward.
export const STAGE_ORDER = PIPELINE_STAGE_ORDER;

// Stages where the borrower CANNOT submit a new showing or offer
// request — they're already committed to a property in motion.
// LOCKSTEP mirror of backend/constants/stages.js SHOWING_OFFER_BLOCKED_STAGES.
// Both files must stay in sync; same convention as doorlyProgramTerms.js.
//
// Boundary per 3c stage-gate spec: allowed in home_shopping only;
// blocked from offer_requested onward.
//
// Frontend hide on Schedule Showing / Request Offer buttons binds to
// this list (Homes.js ListingCard, HomeDetail.js detail page).
// Frontend hiding is UX only — backend rejection on POST /showing +
// POST /offer is the actual gate.
export const SHOWING_OFFER_BLOCKED_STAGES = [
  'offer_requested',
  'offer_submitted',
  'offer_accepted',
  'option_period',
  'under_contract',
  'loan_docs',
  'closing_disclosures',
  'financing',
  'clear_to_close',
  'closing',
  'closed',
  'funded',
];

// Per-stage display labels — derived from STATUS_COLORS so labels stay in
// lockstep with the badges. Use this when you only need the label and not
// the bg/text colors.
export const STAGE_LABELS = Object.fromEntries(
  Object.entries(STATUS_COLORS).map(([k, v]) => [k, v.label])
);

// Per-stage badge bg color — derived from STATUS_COLORS for the same reason.
export const STAGE_COLORS = Object.fromEntries(
  Object.entries(STATUS_COLORS).map(([k, v]) => [k, v.bg])
);

// Stages where the Closings tab in DoorlyDesk should appear. Starts at
// option_period — offer_submitted is intentionally NOT included (Closings
// tab unlocks only after the offer is accepted).
export const CLOSING_STAGES = [
  'option_period',
  'under_contract',
  'loan_docs',
  'closing_disclosures',
  'closing',
  'clear_to_close',
  'closed',
  'funded',
];

// Stages where the listing agent has accepted the offer and pricing flips
// from resale-adjusted to contract-priced. offer_accepted is included even
// though the borrower is still in the LE review flow — contract_purchase_price
// is written by server.js's offer-accept handler at the moment of LA accept
// (and purchase_price_updated_at_offer is stamped), so the price is locked
// from that point forward regardless of whether the borrower has finished
// reviewing. Without offer_accepted here the loan tape / pipeline volume
// shows "R" (requested) for borrowers between LA accept and option_period.
export const POST_ACCEPTANCE_STAGES = [
  'offer_accepted',
  'option_period',
  'closing',
  'clear_to_close',
  'closed',
  'funded',
];

// Stages where the borrower has SmartEstimate-accepted but is still in the
// active home-shopping / offer-pending phase. In this window pricing applies
// the resale adjustment to doorly_purchase_price.
export const HOME_SHOPPING_STAGES = [
  'home_shopping',
  'offer_requested',
  'offer_submitted',
];

// Stages from which Verification's auto-advance should NOT run. The borrower
// has already moved past verification; the verification page should be
// reachable but should not bump the loan stage forward.
//
// in_review removed — ReadyCheck submit owns that transition now; there's
// nothing for Verification's legacy auto-advance hook to do for borrowers
// in that state (they're either auto-approved post-submit or parked in
// adverse_pending pending human review).
//
// adverse_pending added — the file is post-Atlas, awaiting human review.
// Verification page shouldn't auto-bump it forward; only ops can resolve
// it (via manual status PUT or by addressing the underlying overlay).
export const NO_AUTO_ADVANCE_STAGES = [
  'adverse_pending',
  'approved',
  'conditional_approval',
  'home_shopping',
  'offer_requested',
  'offer_submitted',
  'offer_accepted',
  'option_period',
  'closing',
  'clear_to_close',
  'closed',
  'funded',
];

// Stages considered active in the pipeline (not terminal / off-track). Used
// for "active loans" rollups. Intentionally excludes 'closed', 'funded',
// 'on_hold', 'not_moving_forward', and the various pre-application aliases.
// adverse_pending IS active — the file is mid-flight pending human review,
// not declined.
export const ACTIVE_PIPELINE_STAGES = [
  'submitted', 'applied', 'in_review', 'adverse_pending',
  'approved', 'conditional_approval',
  'home_shopping',
  'offer_requested', 'offer_submitted',
  'option_period',
  'under_contract', 'loan_docs', 'closing_disclosures',
  'closing', 'clear_to_close',
];
